import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  LayoutDashboard, ShoppingBag, Users, Settings,
  LogOut, Search, X, Menu, Shirt,
  DollarSign, Package, UserCheck,
  Edit, Trash2, Plus, Eye, AlertTriangle,
  Printer, Lock, RefreshCw, TrendingUp, Check,
  ArrowUpRight, MessageSquare, Send, PhoneCall,
  ChevronLeft,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";

import { db } from "../firebase/config";
import {
  collection, onSnapshot, updateDoc, deleteDoc,
  doc, addDoc, serverTimestamp, query, orderBy,
  limit, writeBatch,
} from "firebase/firestore";

import { useOrders }    from "../hooks/useOrders";
import { useCustomers } from "../hooks/useCustomers";
import { adminLogout }  from "../services/authService";
import { updateOrderStatus, deleteOrder } from "../services/orderService";
import businessConfig   from "../config/businessConfig";

const fmt = n => "$" + Number(n || 0).toLocaleString();
const Badge = ({ text, cls }) => (
  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{text}</span>
);

// ═══════════════════════════════════════════════════════════
// AUDIO ENGINE — MODULE LEVEL
// Must be outside React — functions inside component are
// recreated every render. useEffect(fn,[]) captures them once
// at mount causing stale closures. Module-level = stable.
// ═══════════════════════════════════════════════════════════
let _audioCtx = null;
function _getCtx() {
  try {
    if (!_audioCtx || _audioCtx.state === "closed")
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    return _audioCtx;
  } catch(e) { return null; }
}

// await ctx.resume() — browser suspends AudioContext until
// user gesture. Without await, sound drops silently.
async function _playNewOrderSound(onRing) {
  try {
    const ctx = _getCtx(); if (!ctx) return;
    if (ctx.state === "suspended") await ctx.resume();
    const now = ctx.currentTime;
    [0, 0.28].forEach((delay, i) => {
      const freq = i === 0 ? 880 : 660;
      const osc = ctx.createOscillator(), gain = ctx.createGain(), filter = ctx.createBiquadFilter();
      filter.type = "bandpass"; filter.frequency.value = freq; filter.Q.value = 1.5;
      osc.connect(filter); filter.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(0.6, now + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 1.0);
      osc.start(now + delay); osc.stop(now + delay + 1.1);
    });
    if (onRing) { onRing(true); setTimeout(() => onRing(false), 700); }
  } catch(e) { console.warn("Sound error:", e); }
}

async function _playLoginChime(onRing) {
  try {
    const ctx = _getCtx(); if (!ctx) return;
    if (ctx.state === "suspended") await ctx.resume();
    const now = ctx.currentTime;
    [0, 0.2, 0.4].forEach((delay, i) => {
      const freqs = [523, 659, 784];
      const osc = ctx.createOscillator(), gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freqs[i];
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(0.35, now + delay + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.7);
      osc.start(now + delay); osc.stop(now + delay + 0.8);
    });
    if (onRing) { onRing(true); setTimeout(() => onRing(false), 1200); }
  } catch(e) { console.warn("Chime error:", e); }
}

// ════════════════════════════════════════════════════════════
// ORDER ALERT SOUND — module level, never goes stale
// 3-note ascending chime E5→G5→B5 plays the instant a new
// order arrives. No external file needed — pure Web Audio API.
// ════════════════════════════════════════════════════════════
let _ac = null;
function _getAC() {
  if (!_ac || _ac.state === "closed")
    _ac = new (window.AudioContext || window.webkitAudioContext)();
  return _ac;
}
async function playOrderAlert() {
  try {
    const ctx = _getAC();
    if (ctx.state === "suspended") await ctx.resume();
    const now = ctx.currentTime;
    [[0, 659],[0.18, 784],[0.36, 988]].forEach(([delay, freq]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sine"; osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now + delay);
      gain.gain.linearRampToValueAtTime(0.5, now + delay + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.5);
      osc.start(now + delay); osc.stop(now + delay + 0.6);
    });
  } catch(e) { console.warn("Order alert sound failed:", e); }
}

function timeAgo(ts) {
  if (!ts) return "";
  const date = ts?.toDate ? ts.toDate() : new Date(ts);
  const s = Math.floor((Date.now() - date.getTime()) / 1000);
  if (s < 5) return "Just now";
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d === 1 ? "Yesterday" : `${d}d ago`;
}

const NOTIF_CFG = {
  new_order: { icon:"📦", bg:"bg-blue-50",   border:"border-blue-100",   badge:"bg-blue-100 text-blue-700",    label:"New Order" },
  completed: { icon:"✅", bg:"bg-green-50",  border:"border-green-100",  badge:"bg-green-100 text-green-700",  label:"Completed" },
  cancelled: { icon:"❌", bg:"bg-red-50",    border:"border-red-100",    badge:"bg-red-100 text-red-700",      label:"Cancelled" },
  pending:   { icon:"⏳", bg:"bg-yellow-50", border:"border-yellow-100", badge:"bg-yellow-100 text-yellow-700",label:"Pending"   },
  system:    { icon:"⚙️", bg:"bg-gray-50",   border:"border-gray-100",   badge:"bg-gray-100 text-gray-600",   label:"System"    },
};

const STATUS_COLORS = {
  "Pending":     "bg-yellow-100 text-yellow-700",
  "In Progress": "bg-blue-100 text-blue-700",
  "Completed":   "bg-green-100 text-green-700",
  "Picked Up":   "bg-purple-100 text-purple-700",
  "Cancelled":   "bg-red-100 text-red-700",
};
const CHART_COLORS = ["#4CAF82","#5BA4CF","#E8A838","#A78BFA"];
const DEFAULT_STAFF = [
  { id:"STF-001", name:"James Carter",  role:"Dry Cleaning Specialist", shift:"Morning",   status:"On Duty"  },
  { id:"STF-002", name:"Maria Lopez",   role:"Wash & Fold Operator",    shift:"Morning",   status:"On Duty"  },
  { id:"STF-003", name:"David Kim",     role:"Driver / Pickup",         shift:"Afternoon", status:"Off Duty" },
  { id:"STF-004", name:"Sarah Johnson", role:"Customer Service",        shift:"Morning",   status:"On Duty"  },
  { id:"STF-005", name:"Tom Williams",  role:"Ironing Specialist",      shift:"Afternoon", status:"On Leave" },
];

function printReceipt(order) {
  const win = window.open("","_blank","width=400,height=600");
  win.document.write(`<html><head><title>Receipt</title>
  <style>body{font-family:Arial;padding:30px;font-size:13px}h2{text-align:center}hr{border-top:1px dashed #ccc;margin:12px 0}.row{display:flex;justify-content:space-between;margin:5px 0}.footer{text-align:center;color:#888;font-size:11px;margin-top:20px}</style>
  </head><body>
  <h2>1464 Country Cleaners</h2><p style="text-align:center;color:#666">${businessConfig.phone}</p><hr/>
  <div class="row"><span>Order ID</span><b>${order.orderId||"—"}</b></div>
  <div class="row"><span>Customer</span><b>${order.name||"—"}</b></div>
  <div class="row"><span>Service</span><b>${order.service||"—"}</b></div>
  <div class="row"><span>Status</span><b>${order.status||"—"}</b></div><hr/>
  <div class="row"><b style="font-size:15px">TOTAL</b><b style="font-size:15px">$${Number(order.amount||0).toLocaleString()}</b></div><hr/>
  <div class="footer">Thank you for choosing 1464 Country Cleaners!</div>
  </body></html>`);
  win.document.close(); win.print();
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { orders, loading: ordersLoading, pendingOrders, totalRevenue } = useOrders();
  const { customers, loading: customersLoading } = useCustomers();

  const [section,         setSection]        = useState("overview");
  const [services,        setServices]       = useState(businessConfig.services);
  const [staff,           setStaff]          = useState(DEFAULT_STAFF);
  const [sideOpen,        setSideOpen]       = useState(false);
  const [selectedOrder,   setSelectedOrder]  = useState(null);
  const [filterStatus,    setFilterStatus]   = useState("All");
  const [searchQ,         setSearchQ]        = useState("");
  const [addSvcOpen,      setAddSvcOpen]     = useState(false);
  const [addStaffOpen,    setAddStaffOpen]   = useState(false);
  const [newSvc,          setNewSvc]         = useState({ name:"", price:"", unit:"per item", duration:"24hrs", icon:"👔" });
  const [newStaff,        setNewStaff]       = useState({ name:"", role:"", shift:"Morning", status:"On Duty" });
  const [isTransitioning, setIsTransitioning]= useState(false);
  const [reloading,       setReloading]      = useState(false);

  function navigateTo(id) {
    if (id === section) return;
    setIsTransitioning(true);
    setTimeout(() => { setSection(id); setIsTransitioning(false); }, 250);
  }
  function handleReload() {
    setReloading(true); setIsTransitioning(true);
    setTimeout(() => window.location.reload(), 300);
  }

  // ── Notification state ─────────────────────────────────
  const [notifs,       setNotifs]     = useState([]);
  const [notifLoading, setNotifLoading]= useState(true);
  const [notifFilter,  setNotifFilter] = useState("all");
  const [showNotif,    setShowNotif]   = useState(false);
  const [ringing,      setRinging]     = useState(false);
  const notifPanelRef    = useRef(null);
  const knownNotifIdsRef = useRef(null);
  const prevOrderIdsRef  = useRef(null);
  const setRingingRef    = useRef(setRinging);
  useEffect(() => { setRingingRef.current = setRinging; });
  const unread = notifs.filter(n => !n.read).length;

  // ── Chat / Messages state ──────────────────────────────
  const [conversations,    setConversations]   = useState([]);
  const [activeConvId,     setActiveConvId]    = useState(null);
  const [messages,         setMessages]        = useState([]);
  const [msgInput,         setMsgInput]        = useState("");
  const [convLoading,      setConvLoading]     = useState(true);
  const [msgLoading,       setMsgLoading]      = useState(false);
  const [mobileShowThread, setMobileShowThread]= useState(false);
  const messagesEndRef = useRef(null);
  const msgUnsubRef    = useRef(null);
  const totalUnreadMsgs = conversations.reduce((sum,c) => sum+(c.adminUnread||0), 0);

  // ══════════════════════════════════════════════════════
  // NOTIFICATION LISTENER — single source of truth
  // ══════════════════════════════════════════════════════
  useEffect(() => {
    const q = query(collection(db,"notifications"), orderBy("createdAt","desc"), limit(50));
    const unsub = onSnapshot(q, async (snap) => {
      const data = snap.docs.map(d => ({ id:d.id, ...d.data() }));
      if (knownNotifIdsRef.current === null) {
        knownNotifIdsRef.current = new Set(data.map(n => n.id));
        setNotifs(data); setNotifLoading(false);
        const unreadCount = data.filter(n => !n.read).length;
        if (unreadCount > 0)
          setTimeout(() => _playLoginChime(setRingingRef.current), 1500);
        return;
      }
      const incoming = data.filter(n => !knownNotifIdsRef.current.has(n.id));
      if (incoming.length > 0) {
        incoming.forEach(n => knownNotifIdsRef.current.add(n.id));
        if (incoming.some(n => n.type === "new_order"))
          await _playNewOrderSound(setRingingRef.current);
        incoming.filter(n => n.type === "new_order").forEach(n => {
          toast(t => (
            <div className="flex items-start gap-3">
              <span className="text-2xl">📦</span>
              <div className="flex-1">
                <p className="font-black text-gray-900 text-sm">New Order!</p>
                <p className="text-xs text-gray-600 mt-0.5">{n.message}</p>
                <button onClick={() => { navigateTo("orders"); toast.dismiss(t.id); }}
                  className="mt-2 text-xs bg-indigo-600 text-white px-3 py-1 rounded-lg font-bold hover:bg-indigo-700">
                  View Order →
                </button>
              </div>
              <button onClick={() => toast.dismiss(t.id)} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={14}/></button>
            </div>
          ), { duration:9000, style:{padding:"12px",maxWidth:"340px"} });
        });
      }
      setNotifs(data); setNotifLoading(false);
    }, err => { console.error("Notifications error:",err); setNotifLoading(false); });
    return () => unsub();
  }, []);

  // Detect new orders → play sound + toast + write to Firestore
  useEffect(() => {
    if (ordersLoading) return;
    const currentIds = new Set(orders.map(o => o.id));
    if (prevOrderIdsRef.current === null) { prevOrderIdsRef.current = currentIds; return; }
    orders.filter(o => !prevOrderIdsRef.current.has(o.id)).forEach(order => {
      // 🔔 Play the 3-note chime immediately
      playOrderAlert();
      // 🍞 Show persistent toast
      toast(t => (
        <div className="flex items-start gap-3">
          <span className="text-2xl">📦</span>
          <div className="flex-1">
            <p className="font-black text-gray-900 text-sm">New Order! 🔔</p>
            <p className="text-xs text-gray-600 mt-0.5">{order.name} · {order.service}</p>
            <p className="text-xs font-bold text-indigo-600 mt-0.5">{fmt(order.amount)}</p>
            <button onClick={()=>{navigateTo("orders");toast.dismiss(t.id);}}
              className="mt-2 text-xs bg-indigo-600 text-white px-3 py-1.5 rounded-lg font-bold hover:bg-indigo-700">
              View Order →
            </button>
          </div>
          <button onClick={()=>toast.dismiss(t.id)} className="text-gray-400 hover:text-gray-600 shrink-0"><X size={14}/></button>
        </div>
      ), { duration:10000, style:{padding:"12px",maxWidth:"340px"} });
      // Write to Firestore notifications
      addDoc(collection(db,"notifications"), {
        type:"new_order", title:"New Booking Received 📦",
        message:`${order.name||"A customer"} booked ${order.service} — ${fmt(order.amount)}`,
        orderId:order.id, link:"orders", read:false, createdAt:serverTimestamp(),
      }).catch(e => console.error("Notification write failed:",e));
    });
    prevOrderIdsRef.current = currentIds;
  }, [orders, ordersLoading]);

  // Close notif panel on outside click
  useEffect(() => {
    function outside(e) { if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) setShowNotif(false); }
    if (showNotif) document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [showNotif]);

  async function markNotifRead(id) { try { await updateDoc(doc(db,"notifications",id),{read:true}); } catch(e){} }
  async function markAllNotifsRead() {
    try { const b=writeBatch(db); notifs.filter(n=>!n.read).forEach(n=>b.update(doc(db,"notifications",n.id),{read:true})); await b.commit(); toast.success("All marked as read"); }
    catch(e) { toast.error("Failed"); }
  }
  async function deleteNotif(id) { try { await deleteDoc(doc(db,"notifications",id)); } catch(e){} }
  async function clearAllNotifs() {
    if (!window.confirm("Clear all notifications?")) return;
    try { const b=writeBatch(db); notifs.forEach(n=>b.delete(doc(db,"notifications",n.id))); await b.commit(); toast.success("Cleared"); }
    catch(e) { toast.error("Failed"); }
  }
  function handleNotifClick(notif) {
    if (!notif.read) markNotifRead(notif.id);
    if (notif.link) { navigateTo(notif.link); setShowNotif(false); if(notif.orderId){const t=orders.find(o=>o.id===notif.orderId);if(t)setSelectedOrder(t);} }
  }

  // Conversations listener
  useEffect(() => {
    const q = query(collection(db,"conversations"), orderBy("lastAt","desc"));
    const unsub = onSnapshot(q, snap => { setConversations(snap.docs.map(d=>({id:d.id,...d.data()}))); setConvLoading(false); },
      err => { console.error(err); setConvLoading(false); });
    return () => unsub();
  }, []);

  // Messages listener
  useEffect(() => {
    if (msgUnsubRef.current) { msgUnsubRef.current(); msgUnsubRef.current=null; }
    if (!activeConvId) { setMessages([]); return; }
    setMsgLoading(true);
    const q = query(collection(db,"conversations",activeConvId,"messages"), orderBy("createdAt","asc"), limit(100));
    msgUnsubRef.current = onSnapshot(q, snap => {
      setMessages(snap.docs.map(d=>({id:d.id,...d.data()}))); setMsgLoading(false);
      updateDoc(doc(db,"conversations",activeConvId),{adminUnread:0}).catch(()=>{});
    }, err => { console.error(err); setMsgLoading(false); });
    return () => { if(msgUnsubRef.current) msgUnsubRef.current(); };
  }, [activeConvId]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({behavior:"smooth"}); }, [messages]);

  async function sendAdminMessage() {
    const text = msgInput.trim(); if(!text||!activeConvId) return; setMsgInput("");
    try {
      await addDoc(collection(db,"conversations",activeConvId,"messages"),{text,from:"admin",createdAt:serverTimestamp(),read:false});
      await updateDoc(doc(db,"conversations",activeConvId),{lastMessage:text,lastAt:serverTimestamp()});
    } catch(e) { toast.error("Failed to send"); }
  }

  async function startNewConversation(name,phone,email="") {
    if(!name||!phone){toast.error("Name and phone required");return null;}
    try { const r=await addDoc(collection(db,"conversations"),{customerName:name,customerPhone:phone,customerEmail:email,lastMessage:"",lastAt:serverTimestamp(),adminUnread:0}); return r.id; }
    catch(e) { toast.error("Could not start conversation"); return null; }
  }

  const weekDays   = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const weeklyData  = weekDays.map(day=>({day,orders:orders.filter(o=>o.createdAt?.toDate&&weekDays[o.createdAt.toDate().getDay()]===day).length}));
  const serviceData = businessConfig.services.map(s=>({name:s.name,value:orders.filter(o=>o.service===s.name).length||0})).filter(s=>s.value>0);
  const revenueData = monthNames.map(month=>({month,revenue:orders.filter(o=>o.createdAt?.toDate&&monthNames[o.createdAt.toDate().getMonth()]===month&&o.status==="Completed").reduce((sum,o)=>sum+(o.amount||0),0)}));

  const navItems = [
    {id:"overview", icon:<LayoutDashboard size={18}/>, label:"Overview" },
    {id:"orders",   icon:<ShoppingBag size={18}/>,     label:"Orders"   },
    {id:"customers",icon:<Users size={18}/>,           label:"Customers"},
    {id:"services", icon:<Shirt size={18}/>,           label:"Services" },
    {id:"staff",    icon:<UserCheck size={18}/>,       label:"Staff"    },
    {id:"settings", icon:<Settings size={18}/>,        label:"Settings" },
  ];

  const filteredOrders = orders
    .filter(o=>filterStatus==="All"||o.status===filterStatus)
    .filter(o=>!searchQ||[o.name,o.orderId,o.service,o.phone].some(v=>v?.toLowerCase?.().includes(searchQ.toLowerCase())));

  async function handleLogout() { await adminLogout(); toast.success("Logged out"); navigate("/admin/login"); }
  async function handleStatusChange(docId,newStatus) {
    const r=await updateOrderStatus(docId,newStatus);
    if(r.success){toast.success("Order updated ✅");if(selectedOrder?.id===docId)setSelectedOrder(prev=>({...prev,status:newStatus}));}
    else toast.error("Failed to update");
  }
  async function handleDeleteOrder(docId) {
    if(!window.confirm("Delete this order permanently?")) return;
    const r=await deleteOrder(docId);
    if(r.success){toast.success("Order deleted");setSelectedOrder(null);}else toast.error("Delete failed");
  }
  function handleAddService() {
    if(!newSvc.name||!newSvc.price){toast.error("Name and price required");return;}
    setServices(prev=>[...prev,{...newSvc,id:"SVC-"+Date.now(),price:+newSvc.price,active:true}]);
    setNewSvc({name:"",price:"",unit:"per item",duration:"24hrs",icon:"👔"});setAddSvcOpen(false);toast.success("Service added!");
  }
  function handleAddStaff() {
    if(!newStaff.name||!newStaff.role){toast.error("Name and role required");return;}
    setStaff(prev=>[...prev,{...newStaff,id:"STF-"+Date.now()}]);
    setNewStaff({name:"",role:"",shift:"Morning",status:"On Duty"});setAddStaffOpen(false);toast.success("Staff added!");
  }

  function Sidebar() {
    return (
      <>
        {sideOpen&&<div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={()=>setSideOpen(false)}/>}
        <aside className={`fixed md:relative z-40 inset-y-0 left-0 w-60 bg-gray-900 text-white flex flex-col transition-transform duration-300 ${sideOpen?"translate-x-0 shadow-2xl":"-translate-x-full md:translate-x-0"}`}>
          <div className="p-4 border-b border-gray-700 flex items-center gap-3">
            {businessConfig.logoIsImage
              ?<img src={businessConfig.logo} alt={businessConfig.name} className="h-10 w-auto object-contain shrink-0 rounded-lg bg-white p-0.5"/>
              :<div className="w-9 h-9 rounded-xl bg-indigo-600 flex items-center justify-center font-bold text-sm shrink-0">{businessConfig.logo}</div>}
            <div className="min-w-0"><p className="font-bold text-sm truncate">{businessConfig.shortName}</p><p className="text-xs text-gray-400">Admin Panel</p></div>
            <button className="ml-auto md:hidden text-gray-400 hover:text-white p-1 rounded-lg" onClick={()=>setSideOpen(false)}><X size={18}/></button>
          </div>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {navItems.map(n=>(
              <button key={n.id} onClick={()=>{navigateTo(n.id);setSideOpen(false);}}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${section===n.id?"bg-indigo-600 text-white shadow-md":"text-gray-300 hover:bg-gray-800 hover:text-white"}`}>
                {n.icon}{n.label}
                {n.id==="orders"&&pendingOrders.length>0&&<span className="ml-auto bg-yellow-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">{pendingOrders.length}</span>}
                {n.id==="messages"&&totalUnreadMsgs>0&&<span className="ml-auto bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold">{totalUnreadMsgs}</span>}
              </button>
            ))}
          </nav>
          <div className="p-3 border-t border-gray-700 space-y-1">
            <a href="/" target="_blank" rel="noopener noreferrer" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-300 hover:bg-gray-800 transition-colors"><TrendingUp size={18}/> View Website</a>
            <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-gray-300 hover:bg-red-900/40 hover:text-red-400 transition-colors"><LogOut size={18}/> Logout</button>
          </div>
        </aside>
      </>
    );
  }

  function Topbar() {
    return (
      <div className="h-14 bg-white border-b px-3 sm:px-4 flex items-center justify-between shrink-0 z-20 relative">
        <div className="flex items-center gap-2">
          <button className="md:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-600" onClick={()=>setSideOpen(true)}><Menu size={20}/></button>
          <div>
            <h1 className="font-bold text-gray-800 capitalize text-sm">{section}</h1>
            <p className="text-xs text-gray-400 hidden sm:block">{new Date().toDateString()}</p>
          </div>
        </div>
        {/* Reload button only — bell and message icon removed */}
        <button onClick={handleReload} title="Reload dashboard"
          className="p-2 rounded-xl hover:bg-gray-100 text-gray-500 hover:text-indigo-600 transition-all group">
          <RefreshCw size={17} className={`transition-transform duration-500 ${reloading?"animate-spin text-indigo-600":"group-hover:rotate-180"}`}/>
        </button>
      </div>
    );
  }

  function BottomNav() {
    const items = [
      {id:"overview", icon:<LayoutDashboard size={19}/>, label:"Home"    },
      {id:"orders",   icon:<ShoppingBag size={19}/>,     label:"Orders"  },
      {id:"customers",icon:<Users size={19}/>,           label:"Customers"},
      {id:"settings", icon:<Settings size={19}/>,        label:"Settings"},
    ];
    return (
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 flex">
        {items.map(n=>(
          <button key={n.id} onClick={()=>navigateTo(n.id)}
            className={`flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-[10px] font-semibold transition-colors relative ${section===n.id?"text-indigo-600":"text-gray-400 hover:text-gray-600"}`}>
            {n.id==="orders"&&pendingOrders.length>0&&<span className="absolute top-1 right-1/4 w-4 h-4 bg-red-500 rounded-full text-white text-[9px] flex items-center justify-center font-bold">{pendingOrders.length}</span>}
            <span className={section===n.id?"text-indigo-600":""}>{n.icon}</span>
            {n.label}
            {section===n.id&&<span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-indigo-600 rounded-full"/>}
          </button>
        ))}
      </nav>
    );
  }

  // ══════════════════════════════════════════════════════════
  // OVERVIEW
  // ══════════════════════════════════════════════════════════
  function Overview() {
    const hour = new Date().getHours();
    const greeting = hour<12?"Good Morning":hour<17?"Good Afternoon":"Good Evening";
    const todayCount = orders.filter(o=>{
      if(!o.createdAt?.toDate) return false;
      const d=o.createdAt.toDate(),t=new Date();
      return d.getDate()===t.getDate()&&d.getMonth()===t.getMonth();
    }).length;

    // Nature-inspired card palette: forest green, warm brown, cloud gold, earth clay
    const cards = [
      { label:"Total Revenue", val:ordersLoading?"…":fmt(totalRevenue), sub:"From completed orders", goTo:"orders",
        icon:<DollarSign size={20}/>, bg:"from-[#f0f7ee] to-[#d8edd4]", border:"border-[#a8d5a2]",
        iconBg:"bg-[#4a7c59]", valCl:"text-[#2d5a3d]", subCl:"text-[#4a7c59]", barCl:"bg-[#6aaa7a]", barW:"75%" },
      { label:"Total Orders", val:ordersLoading?"…":orders.length, sub:`${pendingOrders.length} pending`, goTo:"orders",
        icon:<Package size={20}/>, bg:"from-[#fdf8f0] to-[#f5e6c8]", border:"border-[#d4a96a]",
        iconBg:"bg-[#a0522d]", valCl:"text-[#6b3a1f]", subCl:"text-[#a0522d]", barCl:"bg-[#c8834a]", barW:"60%" },
      { label:"Customers", val:customersLoading?"…":customers.length, sub:"Total registered", goTo:"customers",
        icon:<Users size={20}/>, bg:"from-[#fefcf5] to-[#fdf3d0]", border:"border-[#e8d08a]",
        iconBg:"bg-[#b8860b]", valCl:"text-[#7a5c00]", subCl:"text-[#a07800]", barCl:"bg-[#d4a017]", barW:"45%" },
      { label:"Pending", val:ordersLoading?"…":pendingOrders.length,
        sub:pendingOrders.length>0?"Needs attention!":"All clear ✓", goTo:"orders",
        icon:<AlertTriangle size={20}/>,
        bg:pendingOrders.length>0?"from-[#fdf2ee] to-[#f8ddd4]":"from-[#f5f3f0] to-[#ede8e3]",
        border:pendingOrders.length>0?"border-[#d4896a]":"border-[#c8b8a8]",
        iconBg:pendingOrders.length>0?"bg-[#b85c38]":"bg-[#8b7355]",
        valCl:pendingOrders.length>0?"text-[#7a3018]":"text-[#5a4a3a]",
        subCl:pendingOrders.length>0?"text-[#b85c38]":"text-[#7a6a5a]",
        barCl:pendingOrders.length>0?"bg-[#c8704a]":"bg-[#a89070]", barW:pendingOrders.length>0?"35%":"5%" },
    ];

    return (
      <div className="space-y-4">
        {/* Welcome banner — warm forest & golden sky nature theme */}
        <div className="rounded-2xl p-4 sm:p-6 text-white relative overflow-hidden"
          style={{background:"linear-gradient(135deg, #3d6b4f 0%, #5a8a4a 35%, #8b7355 65%, #a07840 100%)"}}>
          {/* Decorative cloud circles */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-white/8 rounded-full -translate-y-14 translate-x-14"/>
          <div className="absolute bottom-0 left-0 w-28 h-28 bg-white/5 rounded-full translate-y-10 -translate-x-10"/>
          <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white/5 rounded-full"/>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">🌿</span>
              <p className="text-green-100 text-xs font-medium">{greeting}, Admin 👋</p>
            </div>
            <h2 className="text-lg sm:text-2xl font-black leading-tight text-white drop-shadow">{businessConfig.name}</h2>
            <p className="text-amber-100/80 text-xs mt-1 hidden sm:block">{new Date().toLocaleDateString("en-US",{weekday:"long",year:"numeric",month:"long",day:"numeric"})}</p>
            <div className="flex gap-3 mt-4">
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2.5 text-center border border-white/20 shadow-inner">
                <p className="text-[10px] text-amber-100 font-semibold tracking-wide uppercase">Today's Orders</p>
                <p className="text-2xl font-black">{todayCount}</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2.5 text-center border border-white/20 shadow-inner">
                <p className="text-[10px] text-amber-100 font-semibold tracking-wide uppercase">Staff On Duty</p>
                <p className="text-2xl font-black">{DEFAULT_STAFF.filter(s=>s.status==="On Duty").length}</p>
              </div>
              <div className="bg-white/15 backdrop-blur-sm rounded-xl px-3 py-2.5 text-center border border-white/20 shadow-inner">
                <p className="text-[10px] text-amber-100 font-semibold tracking-wide uppercase">Pending</p>
                <p className="text-2xl font-black">{pendingOrders.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* KPI Cards — nature earth palette, 2 col mobile, 4 desktop */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {cards.map(k=>(
            <div key={k.label} onClick={()=>navigateTo(k.goTo)} role="button" tabIndex={0}
              onKeyDown={e=>e.key==="Enter"&&navigateTo(k.goTo)}
              className={`bg-gradient-to-br ${k.bg} border-2 ${k.border} rounded-2xl p-3 sm:p-5 cursor-pointer hover:shadow-xl hover:-translate-y-1 transition-all select-none group`}>
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl ${k.iconBg} text-white flex items-center justify-center shadow-md group-hover:scale-110 transition-transform`}>{k.icon}</div>
                <ArrowUpRight size={14} className={k.subCl}/>
              </div>
              <p className={`text-2xl sm:text-3xl font-black ${k.valCl} mb-0.5`}>{k.val}</p>
              <p className={`text-[10px] sm:text-xs font-bold uppercase tracking-wide ${k.valCl} mb-1`}>{k.label}</p>
              <p className={`text-[10px] sm:text-xs ${k.subCl}`}>{k.sub}</p>
              <div className="mt-3 h-1.5 bg-white/60 rounded-full overflow-hidden">
                <div className={`h-full ${k.barCl} rounded-full transition-all duration-700`} style={{width:k.barW}}/>
              </div>
              <p className={`text-[10px] ${k.subCl} mt-1.5 font-bold opacity-0 group-hover:opacity-100 transition-opacity`}>Tap to view →</p>
            </div>
          ))}
        </div>

        {/* Quick actions — nature tones */}
        <div className="grid grid-cols-2 gap-2 sm:gap-3">
          {[
            {label:"All Orders",   icon:"📦", cls:"bg-[#fdf8f0] text-[#a0522d] border-[#d4a96a] hover:bg-[#f5e6c8]", go:"orders"   },
            {label:"Customers",    icon:"👥", cls:"bg-[#f0f7ee] text-[#2d5a3d] border-[#a8d5a2] hover:bg-[#d8edd4]", go:"customers"},
            {label:"Services",     icon:"🧥", cls:"bg-[#fefcf5] text-[#7a5c00] border-[#e8d08a] hover:bg-[#fdf3d0]", go:"services" },
            {label:"Staff",        icon:"👔", cls:"bg-[#f5f3f0] text-[#5a4a3a] border-[#c8b8a8] hover:bg-[#ede8e3]", go:"staff"    },
          ].map(a=>(
            <button key={a.label} onClick={()=>navigateTo(a.go)}
              className={`flex items-center gap-2 px-3 py-3 rounded-xl border-2 text-sm font-bold transition-all hover:shadow-md hover:-translate-y-0.5 ${a.cls}`}>
              <span className="text-lg">{a.icon}</span>{a.label}
            </button>
          ))}
        </div>

        {/* Charts */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-xs sm:text-sm text-gray-700">Monthly Revenue</h3>
              <span className="text-[10px] bg-emerald-50 text-emerald-600 border border-emerald-200 px-2 py-0.5 rounded-full font-semibold">Live</span>
            </div>
            <p className="text-xs text-gray-400 mb-3">Completed orders only</p>
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={revenueData.some(d=>d.revenue>0)?revenueData:monthNames.slice(0,6).map(m=>({month:m,revenue:0}))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
                <XAxis dataKey="month" tick={{fontSize:9}}/>
                <YAxis tick={{fontSize:9}} tickFormatter={v=>`$${(v/1000).toFixed(0)}k`}/>
                <Tooltip formatter={v=>[fmt(v),"Revenue"]} contentStyle={{borderRadius:"12px",border:"none"}}/>
                <Bar dataKey="revenue" fill="#4CAF82" radius={[6,6,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-bold text-xs sm:text-sm text-gray-700">Service Breakdown</h3>
              <span className="text-[10px] bg-sky-50 text-sky-600 border border-sky-200 px-2 py-0.5 rounded-full font-semibold">Real data</span>
            </div>
            <p className="text-xs text-gray-400 mb-3">Based on bookings</p>
            {serviceData.length===0?(
              <div className="h-40 flex flex-col items-center justify-center text-gray-400 text-sm gap-2">
                <span className="text-3xl">🌱</span><p>No order data yet</p>
              </div>
            ):(
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={serviceData} cx="50%" cy="50%" outerRadius={60} dataKey="value"
                    label={e=>`${e.name.split(" ")[0]}`} labelLine={false} fontSize={9}>
                    {serviceData.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip contentStyle={{borderRadius:"12px",border:"none"}}/>
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Weekly */}
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="font-bold text-xs sm:text-sm text-gray-700 mb-1">Orders This Week</h3>
          <p className="text-xs text-gray-400 mb-3">Real-time order frequency</p>
          <ResponsiveContainer width="100%" height={130}>
            <LineChart data={weeklyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6"/>
              <XAxis dataKey="day" tick={{fontSize:10}}/>
              <YAxis tick={{fontSize:10}} allowDecimals={false}/>
              <Tooltip contentStyle={{borderRadius:"12px",border:"none"}}/>
              <Line type="monotone" dataKey="orders" stroke="#4CAF82" strokeWidth={2.5} dot={{fill:"#4CAF82",r:3}} activeDot={{r:5}}/>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Recent orders — card list on mobile */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
          <div className="p-4 border-b flex justify-between items-center">
            <div className="flex items-center gap-2"><span>📋</span><h3 className="font-bold text-sm text-gray-800">Recent Orders</h3></div>
            <button onClick={()=>navigateTo("orders")} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 py-1.5 rounded-xl font-bold hover:bg-emerald-100 flex items-center gap-1">
              View all<ArrowUpRight size={11}/>
            </button>
          </div>
          {ordersLoading?(
            <div className="p-8 text-center"><RefreshCw size={20} className="animate-spin text-emerald-600 mx-auto mb-2"/></div>
          ):orders.length===0?(
            <div className="p-10 text-center"><span className="text-4xl mb-3 block">🌿</span><p className="text-gray-500 font-semibold">No orders yet</p></div>
          ):(
            <>
              {/* Mobile cards */}
              <div className="sm:hidden divide-y">
                {orders.slice(0,5).map(o=>(
                  <div key={o.id} onClick={()=>navigateTo("orders")} className="p-4 hover:bg-gray-50 cursor-pointer">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs font-bold text-emerald-700">{o.orderId}</span>
                      <Badge text={o.status} cls={STATUS_COLORS[o.status]||"bg-gray-100 text-gray-500"}/>
                    </div>
                    <p className="font-semibold text-sm text-gray-800">{o.name}</p>
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500">{o.service}</p>
                      <p className="font-bold text-emerald-700 text-sm">{fmt(o.amount)}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-xs text-gray-500">
                    {["Order ID","Customer","Service","Total","Status"].map(h=>(
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {orders.slice(0,5).map(o=>(
                      <tr key={o.id} onClick={()=>navigateTo("orders")} className="border-t hover:bg-emerald-50/30 cursor-pointer">
                        <td className="px-4 py-3 font-mono text-xs font-bold text-emerald-700">{o.orderId}</td>
                        <td className="px-4 py-3 font-semibold">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold shrink-0">{o.name?.[0]||"?"}</div>
                            {o.name}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{o.service}</td>
                        <td className="px-4 py-3 font-bold text-emerald-700">{fmt(o.amount)}</td>
                        <td className="px-4 py-3"><Badge text={o.status} cls={STATUS_COLORS[o.status]||"bg-gray-100 text-gray-500"}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // ORDERS
  // ══════════════════════════════════════════════════════════
  function Orders() {
    return (
      <div className="space-y-4">
        {/* Search + filters */}
        <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
          <Search size={15} className="text-gray-400 shrink-0"/>
          <input value={searchQ} onChange={e=>setSearchQ(e.target.value)}
            placeholder="Search name, ID, service…" className="text-sm outline-none flex-1 min-w-0"/>
          {searchQ&&<button onClick={()=>setSearchQ("")} className="text-gray-400"><X size={14}/></button>}
        </div>
        {/* Filter chips — horizontally scrollable on mobile */}
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {["All","Pending","In Progress","Completed","Picked Up","Cancelled"].map(s=>(
            <button key={s} onClick={()=>setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors shrink-0 ${filterStatus===s?"bg-indigo-600 text-white shadow-sm":"bg-white border text-gray-600 hover:bg-indigo-50"}`}>
              {s}
              {s==="Pending"&&pendingOrders.length>0&&<span className="ml-1 bg-yellow-500 text-white text-[10px] px-1 py-0.5 rounded-full font-bold">{pendingOrders.length}</span>}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          {ordersLoading?(
            <div className="p-10 text-center"><RefreshCw size={24} className="animate-spin text-indigo-600 mx-auto mb-2"/><p className="text-gray-400 text-sm">Loading…</p></div>
          ):filteredOrders.length===0?(
            <div className="p-10 text-center"><span className="text-4xl block mb-3">📭</span><p className="text-gray-500 font-semibold">{orders.length===0?"No orders yet":"No orders match your filter"}</p></div>
          ):(
            <>
              <div className="px-4 py-3 border-b bg-gray-50">
                <p className="text-xs text-gray-500 font-semibold">{filteredOrders.length} order{filteredOrders.length!==1?"s":""} found</p>
              </div>

              {/* Mobile: card list */}
              <div className="sm:hidden divide-y">
                {filteredOrders.map(o=>(
                  <div key={o.id} className="p-4 hover:bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-mono text-xs font-bold text-indigo-600">{o.orderId}</span>
                        <p className="font-semibold text-sm text-gray-800 mt-0.5">{o.name}</p>
                        <p className="text-xs text-gray-400">{o.phone}</p>
                      </div>
                      <Badge text={o.status} cls={STATUS_COLORS[o.status]||"bg-gray-100 text-gray-500"}/>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs text-gray-500">{o.service}</p>
                      <p className="font-black text-indigo-600">{fmt(o.amount)}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={()=>setSelectedOrder(o)} className="flex-1 py-2 rounded-xl bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center justify-center gap-1"><Eye size={12}/>View</button>
                      <select value={o.status} onChange={e=>handleStatusChange(o.id,e.target.value)}
                        className="flex-1 text-xs border rounded-xl px-2 py-2 text-gray-600 focus:outline-none bg-white">
                        {["Pending","In Progress","Completed","Picked Up","Cancelled"].map(s=><option key={s}>{s}</option>)}
                      </select>
                      <button onClick={()=>handleDeleteOrder(o.id)} className="p-2 rounded-xl bg-red-50 text-red-400"><Trash2 size={13}/></button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-xs text-gray-500 border-b">
                    {["Order ID","Customer","Service","Date","Status","Actions"].map(h=>(
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredOrders.map(o=>(
                      <tr key={o.id} onClick={()=>setSelectedOrder(o)} className="border-b hover:bg-indigo-50/40 cursor-pointer group">
                        <td className="px-4 py-3 font-mono text-xs font-bold text-indigo-600">{o.orderId}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-black shrink-0">{o.name?.[0]||"?"}</div>
                            <div><p className="font-semibold text-sm">{o.name}</p><p className="text-xs text-gray-400">{o.phone}</p></div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{o.service}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{o.date}</td>
                        <td className="px-4 py-3"><Badge text={o.status} cls={STATUS_COLORS[o.status]||"bg-gray-100 text-gray-500"}/></td>
                        <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <button onClick={()=>setSelectedOrder(o)} className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"><Eye size={13}/></button>
                            <select value={o.status} onChange={e=>handleStatusChange(o.id,e.target.value)}
                              className="text-xs border rounded-lg px-1.5 py-1 text-gray-600 focus:outline-none bg-white">
                              {["Pending","In Progress","Completed","Picked Up","Cancelled"].map(s=><option key={s}>{s}</option>)}
                            </select>
                            <button onClick={()=>handleDeleteOrder(o.id)} className="p-1.5 rounded-lg bg-red-50 text-red-400"><Trash2 size={13}/></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Order detail modal */}
        {selectedOrder&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg relative shadow-2xl max-h-[92vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl z-10">
                <div>
                  <h3 className="font-black text-lg text-gray-900">Order Details</h3>
                  <p className="text-xs text-indigo-600 font-bold mt-0.5">{selectedOrder.orderId}</p>
                </div>
                <button onClick={()=>setSelectedOrder(null)} className="text-gray-400 hover:text-gray-600 p-2 rounded-xl hover:bg-gray-100"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100">
                  <p className="text-xs font-bold text-indigo-500 uppercase tracking-wide mb-3">👤 Customer</p>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-xl font-black">{selectedOrder.name?.[0]||"?"}</div>
                    <div><p className="font-black text-lg text-gray-900">{selectedOrder.name}</p><p className="text-gray-500 text-xs">{selectedOrder.email||"No email"}</p></div>
                  </div>
                  <div className="space-y-2">
                    <a href={`tel:${selectedOrder.phone}`} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-indigo-100 hover:bg-indigo-50 transition-all">
                      <span>📞</span><div><p className="text-[10px] text-gray-400">Phone</p><p className="font-bold text-indigo-600 text-sm">{selectedOrder.phone||"—"}</p></div>
                    </a>
                    {selectedOrder.phone&&(
                      <a href={`https://wa.me/${selectedOrder.phone.replace(/\D/g,"")}?text=Hi ${selectedOrder.name}, regarding order ${selectedOrder.orderId}.`}
                        target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-green-100 hover:bg-green-50 transition-all">
                        <span>💬</span><div><p className="text-[10px] text-gray-400">WhatsApp</p><p className="font-bold text-green-600 text-sm">Send message</p></div>
                      </a>
                    )}
                    {selectedOrder.address&&(
                      <a href={`https://www.google.com/maps/search/${encodeURIComponent(selectedOrder.address)}`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-orange-100 hover:bg-orange-50 transition-all">
                        <span>📍</span><div><p className="text-[10px] text-gray-400">Address</p><p className="font-bold text-orange-600 text-sm">{selectedOrder.address}</p></div>
                      </a>
                    )}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">📦 Order Info</p>
                  <div className="space-y-2">
                    {[["Service",selectedOrder.service],["Date",selectedOrder.date],["Amount",fmt(selectedOrder.amount)],["Payment",selectedOrder.paymentStatus||"Pending"]].map(([k,v])=>(
                      <div key={k} className="flex justify-between items-center border-b border-gray-200 pb-2 last:border-0">
                        <span className="text-gray-500 text-sm">{k}</span>
                        <span className="font-bold text-gray-800 text-sm">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {selectedOrder.notes&&selectedOrder.notes.trim()&&(
                  <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
                    <p className="text-xs font-bold text-amber-600 uppercase tracking-wide mb-2">💬 Notes</p>
                    <p className="text-gray-700 text-sm">{selectedOrder.notes}</p>
                  </div>
                )}
                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">🔄 Update Status</p>
                  <div className="grid grid-cols-3 gap-2">
                    {["Pending","In Progress","Completed","Picked Up","Cancelled"].map(s=>(
                      <button key={s} onClick={()=>handleStatusChange(selectedOrder.id,s)}
                        className={`px-2 py-2 rounded-xl text-xs font-bold transition-all ${selectedOrder.status===s?STATUS_COLORS[s]+" ring-2 ring-offset-1 ring-indigo-400 scale-105":"bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={()=>printReceipt(selectedOrder)} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black text-sm hover:bg-indigo-700 flex items-center justify-center gap-2">
                    <Printer size={15}/> Print Receipt
                  </button>
                  <button onClick={()=>handleDeleteOrder(selectedOrder.id)} className="px-4 py-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 border border-red-200">
                    <Trash2 size={16}/>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // CUSTOMERS
  // ══════════════════════════════════════════════════════════
  function Customers() {
    const [cs,         setCs]         = useState("");
    const [selCust,    setSelCust]    = useState(null);
    const [notifyOpen, setNotifyOpen] = useState(false);

    const filtered = customers.filter(c =>
      !cs || c.name?.toLowerCase().includes(cs.toLowerCase()) ||
      c.phone?.includes(cs) || c.email?.toLowerCase().includes(cs.toLowerCase())
    );
    const getCustomerOrders = name => orders.filter(o=>o.name===name);

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 bg-white border rounded-xl px-3 py-2 shadow-sm">
          <Search size={15} className="text-gray-400 shrink-0"/>
          <input value={cs} onChange={e=>setCs(e.target.value)} placeholder="Search customers…" className="text-sm outline-none flex-1"/>
          {cs&&<button onClick={()=>setCs("")} className="text-gray-400"><X size={14}/></button>}
        </div>

        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b flex items-center justify-between">
            <h3 className="font-bold text-sm">All Customers ({customersLoading?"…":filtered.length})</h3>
          </div>
          {customersLoading?(
            <div className="p-10 text-center"><RefreshCw size={24} className="animate-spin text-indigo-600 mx-auto mb-2"/></div>
          ):filtered.length===0?(
            <div className="p-10 text-center"><span className="text-4xl block mb-3">👥</span><p className="text-gray-500 font-semibold">{customers.length===0?"No customers yet":"No match"}</p></div>
          ):(
            <>
              {/* Mobile: card list */}
              <div className="sm:hidden divide-y">
                {filtered.map(c=>{
                  const custOrders = getCustomerOrders(c.name);
                  return (
                    <div key={c.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-11 h-11 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-lg font-black shrink-0">{c.name?.[0]||"?"}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-800 truncate">{c.name}</p>
                          <p className="text-xs text-gray-400">{c.phone}</p>
                        </div>
                        <Badge text={c.status||"Active"} cls={c.status==="VIP"?"bg-yellow-100 text-yellow-700":c.status==="Inactive"?"bg-gray-100 text-gray-500":"bg-green-100 text-green-700"}/>
                      </div>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-xs text-gray-500">{custOrders.length} orders · {c.email||"No email"}</p>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={()=>setSelCust(c)} className="flex-1 py-2 rounded-xl bg-indigo-50 text-indigo-600 text-xs font-bold flex items-center justify-center gap-1"><Eye size={12}/>View</button>
                        <button onClick={()=>{setSelCust(c);setNotifyOpen(true);}} className="flex-1 py-2 rounded-xl bg-green-50 text-green-700 text-xs font-bold">👕 Ready</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop: table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="bg-gray-50 text-xs text-gray-500">
                    {["Customer","Phone","Orders","Status","Joined","Actions"].map(h=>(
                      <th key={h} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filtered.map(c=>{
                      const custOrders=getCustomerOrders(c.name);
                      return (
                        <tr key={c.id} onClick={()=>setSelCust(c)} className="border-t hover:bg-indigo-50/30 cursor-pointer group">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-black shrink-0">{c.name?.[0]||"?"}</div>
                              <div><p className="font-semibold">{c.name}</p><p className="text-xs text-gray-400">{c.email||"No email"}</p></div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600">{c.phone}</td>
                          <td className="px-4 py-3 text-center font-black text-indigo-600">{custOrders.length||c.orders||0}</td>
                          <td className="px-4 py-3"><Badge text={c.status||"Active"} cls={c.status==="VIP"?"bg-yellow-100 text-yellow-700":c.status==="Inactive"?"bg-gray-100 text-gray-500":"bg-green-100 text-green-700"}/></td>
                          <td className="px-4 py-3 text-xs text-gray-400">{c.createdAt?.toDate?c.createdAt.toDate().toLocaleDateString():"—"}</td>
                          <td className="px-4 py-3" onClick={e=>e.stopPropagation()}>
                            <div className="flex gap-1">
                              <button onClick={()=>setSelCust(c)} className="p-1.5 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100"><Eye size={13}/></button>
                              <button onClick={()=>{setSelCust(c);setNotifyOpen(true);}} className="px-2 py-1.5 rounded-lg bg-green-50 text-green-700 text-xs font-bold">👕 Ready</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Customer profile modal */}
        {selCust&&!notifyOpen&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-lg shadow-2xl max-h-[92vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b px-5 py-4 flex items-center justify-between rounded-t-3xl sm:rounded-t-2xl z-10">
                <h3 className="font-black text-lg text-gray-900">Customer Profile</h3>
                <button onClick={()=>setSelCust(null)} className="text-gray-400 hover:text-gray-600 p-2 rounded-xl hover:bg-gray-100"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-4 border border-indigo-100">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-2xl bg-indigo-600 text-white flex items-center justify-center text-2xl font-black">{selCust.name?.[0]||"?"}</div>
                    <div><p className="font-black text-xl">{selCust.name}</p><Badge text={selCust.status||"Active"} cls={selCust.status==="VIP"?"bg-yellow-100 text-yellow-700":selCust.status==="Inactive"?"bg-gray-100 text-gray-500":"bg-green-100 text-green-700"}/></div>
                  </div>
                  <div className="space-y-2">
                    <a href={`tel:${selCust.phone}`} className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-indigo-100 hover:bg-indigo-50 transition-all">
                      <span>📞</span><div><p className="text-[10px] text-gray-400">Phone</p><p className="font-bold text-indigo-600 text-sm">{selCust.phone||"—"}</p></div>
                    </a>
                    {selCust.phone&&(
                      <a href={`https://wa.me/${selCust.phone.replace(/\D/g,"")}?text=Hi ${selCust.name}, this is 1464 Country Cleaners!`} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-3 bg-white rounded-xl px-3 py-2.5 border border-green-100 hover:bg-green-50 transition-all">
                        <span>💬</span><div><p className="text-[10px] text-gray-400">WhatsApp</p><p className="font-bold text-green-600 text-sm">Send message</p></div>
                      </a>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[
                    {label:"Orders",val:getCustomerOrders(selCust.name).length||selCust.orders||0,color:"text-indigo-600"},
                    {label:"Spent",val:fmt(selCust.spent||0),color:"text-emerald-600"},
                    {label:"Joined",val:selCust.createdAt?.toDate?selCust.createdAt.toDate().toLocaleDateString():"—",color:"text-amber-600"},
                  ].map(s=>(
                    <div key={s.label} className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
                      <p className={`font-black text-base ${s.color}`}>{s.val}</p>
                      <p className="text-[10px] text-gray-400 font-medium mt-0.5">{s.label}</p>
                    </div>
                  ))}
                </div>
                {getCustomerOrders(selCust.name).length>0&&(
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">📦 Order History</p>
                    <div className="space-y-2">
                      {getCustomerOrders(selCust.name).slice(0,5).map(o=>(
                        <div key={o.id} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
                          <div><p className="font-bold text-sm">{o.service}</p><p className="text-xs text-gray-400">{o.orderId} · {o.date}</p></div>
                          <div className="text-right"><p className="font-black text-indigo-600">{fmt(o.amount)}</p><Badge text={o.status} cls={STATUS_COLORS[o.status]||"bg-gray-100 text-gray-500"}/></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <button onClick={()=>setNotifyOpen(true)} className="w-full bg-green-600 text-white py-3.5 rounded-xl font-black text-sm hover:bg-green-700 flex items-center justify-center gap-2">
                  👕 Notify — Clothes Are Ready
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Notify modal */}
        {notifyOpen&&selCust&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-md shadow-2xl">
              <div className="bg-green-600 rounded-t-3xl sm:rounded-t-2xl px-5 py-4 text-white flex items-center justify-between">
                <div><h3 className="font-black text-lg">👕 Clothes Are Ready!</h3><p className="text-green-100 text-sm">Notify {selCust.name}</p></div>
                <button onClick={()=>setNotifyOpen(false)} className="text-white/70 hover:text-white"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3">
                {[
                  { cls:"bg-green-50 border-green-200", icon:"💬", iconBg:"bg-green-500", title:"WhatsApp", desc:"Fastest response", phone:selCust.phone,
                    onClick:()=>{ window.open(`https://wa.me/${(selCust.phone||"").replace(/\D/g,"")}?text=${encodeURIComponent(`Hi ${selCust.name}, your clothes at 1464 Country Cleaners are ready! 🎉`)}`,"_blank"); setNotifyOpen(false); } },
                  { cls:"bg-sky-50 border-sky-200", icon:"💬", iconBg:"bg-sky-500", title:"Send SMS", desc:"Text message", phone:selCust.phone,
                    onClick:()=>{ window.open(`sms:${selCust.phone}?body=Hi ${selCust.name}, clothes ready at 1464 Country Cleaners!`); setNotifyOpen(false); } },
                ].map(item=>(
                  <button key={item.title} onClick={item.onClick}
                    className={`w-full flex items-center gap-4 p-4 rounded-2xl border-2 hover:opacity-90 transition-all ${item.cls}`}>
                    <div className={`w-12 h-12 rounded-xl ${item.iconBg} flex items-center justify-center text-white text-2xl`}>{item.icon}</div>
                    <div className="flex-1 text-left">
                      <p className="font-black text-gray-800">{item.title}</p>
                      <p className="text-gray-500 text-xs">{item.desc}</p>
                      <p className="text-gray-400 text-xs font-semibold mt-0.5">{item.phone}</p>
                    </div>
                    <ArrowUpRight size={16} className="text-gray-400"/>
                  </button>
                ))}
                <a href={`tel:${selCust.phone}`}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl bg-indigo-50 border-2 border-indigo-200 hover:opacity-90 transition-all">
                  <div className="w-12 h-12 rounded-xl bg-indigo-500 flex items-center justify-center text-white text-2xl">📞</div>
                  <div className="flex-1 text-left"><p className="font-black text-gray-800">Call Customer</p><p className="text-gray-500 text-xs">{selCust.phone}</p></div>
                  <ArrowUpRight size={16} className="text-gray-400"/>
                </a>
                <button onClick={()=>setNotifyOpen(false)} className="w-full border-2 border-gray-200 text-gray-500 py-3 rounded-xl font-semibold text-sm hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // SERVICES
  // ══════════════════════════════════════════════════════════
  function Services() {
    const [editSvc,    setEditSvc]    = useState(null);
    const [editForm,   setEditForm]   = useState({});
    const [previewSvc, setPreviewSvc] = useState(null);

    function openEdit(s) { setEditForm({...s}); setEditSvc(s.id); }
    function saveEdit() {
      setServices(prev=>prev.map(x=>x.id===editSvc?{...editForm,price:Number(editForm.price)}:x));
      setEditSvc(null); toast.success("Service updated ✅");
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-black text-base sm:text-lg text-gray-800">Services</h3>
            <p className="text-xs text-gray-400 mt-0.5">Control what shows on your website</p>
          </div>
          <button onClick={()=>setAddSvcOpen(true)} className="flex items-center gap-1.5 bg-indigo-600 text-white px-3 py-2 rounded-xl text-sm font-bold hover:bg-indigo-700 shadow-md">
            <Plus size={14}/> Add
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            {label:"Total",   val:services.length,                    color:"text-indigo-600",bg:"bg-indigo-50",border:"border-indigo-100"},
            {label:"Active",  val:services.filter(s=>s.active).length,color:"text-green-600", bg:"bg-green-50", border:"border-green-100" },
            {label:"Bookings",val:orders.length,                      color:"text-amber-600", bg:"bg-amber-50", border:"border-amber-100" },
          ].map(s=>(
            <div key={s.label} className={`${s.bg} border ${s.border} rounded-2xl p-3 text-center`}>
              <p className={`text-xl sm:text-2xl font-black ${s.color}`}>{s.val}</p>
              <p className="text-[10px] sm:text-xs text-gray-500 font-medium mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Service cards — 1 col on mobile, 2 on sm, 3 on lg */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {services.map(s=>{
            const bookings = orders.filter(o=>o.service===s.name).length;
            const revenue  = orders.filter(o=>o.service===s.name&&o.status==="Completed").reduce((t,o)=>t+(o.amount||0),0);
            return (
              <div key={s.id} className={`bg-white rounded-2xl border-2 overflow-hidden transition-all hover:shadow-lg ${s.active?"border-gray-100 hover:border-indigo-200":"border-dashed border-gray-200 opacity-60"}`}>
                <div className={`h-1.5 w-full ${s.active?"bg-gradient-to-r from-indigo-500 to-purple-500":"bg-gray-200"}`}/>
                <div className="p-4 sm:p-5">
                  <div className="flex justify-between items-start mb-3">
                    <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-2xl">{s.icon}</div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" checked={s.active} onChange={()=>{setServices(prev=>prev.map(x=>x.id===s.id?{...x,active:!x.active}:x));toast.success(`${s.name} ${!s.active?"activated":"deactivated"}`);}} className="sr-only peer"/>
                      <div className="w-10 h-6 bg-gray-300 peer-checked:bg-indigo-600 rounded-full transition-colors relative">
                        <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${s.active?"left-5":"left-1"}`}/>
                      </div>
                    </label>
                  </div>
                  <h4 className="font-black text-base text-gray-900">{s.name}</h4>
                  <p className="text-indigo-600 font-black text-xl mt-1">${Number(s.price).toLocaleString()}<span className="text-xs text-gray-400 font-normal ml-1">{s.unit}</span></p>
                  <p className="text-xs text-gray-400 mt-1 mb-3">⏱ {s.duration}</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-indigo-50 rounded-xl p-2 text-center"><p className="font-black text-indigo-700">{bookings}</p><p className="text-[10px] text-indigo-500">Bookings</p></div>
                    <div className="bg-emerald-50 rounded-xl p-2 text-center"><p className="font-black text-emerald-700 text-sm">${revenue.toLocaleString()}</p><p className="text-[10px] text-emerald-500">Revenue</p></div>
                  </div>
                  <div className={`text-center py-1.5 rounded-xl text-xs font-bold mb-3 ${s.active?"bg-green-50 text-green-700 border border-green-200":"bg-gray-50 text-gray-500 border border-gray-200"}`}>
                    {s.active?"✅ Visible":"❌ Hidden"}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={()=>openEdit(s)} className="flex-1 flex items-center justify-center gap-1 text-xs border-2 border-indigo-200 text-indigo-600 py-2 rounded-xl hover:bg-indigo-50 font-bold"><Edit size={12}/>Edit</button>
                    <button onClick={()=>setPreviewSvc(s)} className="flex-1 flex items-center justify-center gap-1 text-xs border-2 border-gray-200 text-gray-600 py-2 rounded-xl hover:bg-gray-50 font-bold"><Eye size={12}/>Preview</button>
                    <button onClick={()=>{if(window.confirm(`Delete ${s.name}?`)){setServices(prev=>prev.filter(x=>x.id!==s.id));toast.success("Removed");}}} className="p-2 rounded-xl bg-red-50 text-red-400 border-2 border-red-100"><Trash2 size={13}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Add service modal */}
        {addSvcOpen&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
              <div className="bg-indigo-600 rounded-t-3xl sm:rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
                <h3 className="font-black text-lg">Add New Service</h3>
                <button onClick={()=>setAddSvcOpen(false)} className="text-white/70 hover:text-white"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {[["Service Name *","name","text"],["Price ($) *","price","number"],["Duration","duration","text"],["Icon emoji","icon","text"]].map(([ph,k,t])=>(
                  <div key={k}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{ph}</label>
                    <input type={t} placeholder={ph} value={newSvc[k]} onChange={e=>setNewSvc({...newSvc,[k]:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
                  </div>
                ))}
                <div>
                  <label className="block text-xs text-gray-500 font-semibold mb-1">Unit</label>
                  <select value={newSvc.unit} onChange={e=>setNewSvc({...newSvc,unit:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400">
                    {["per kg","per item","per piece","per pair","per lb"].map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
                <button onClick={handleAddService} className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-black hover:bg-indigo-700 mt-2">Add Service →</button>
              </div>
            </div>
          </div>
        )}

        {/* Edit service modal */}
        {editSvc&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
              <div className="bg-indigo-600 rounded-t-3xl sm:rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
                <h3 className="font-black text-lg">Edit Service</h3>
                <button onClick={()=>setEditSvc(null)} className="text-white/70 hover:text-white"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {[["Name","name","text"],["Price ($)","price","number"],["Duration","duration","text"],["Icon","icon","text"],["Description","desc","text"]].map(([ph,k,t])=>(
                  <div key={k}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{ph}</label>
                    <input type={t} placeholder={ph} value={editForm[k]||""} onChange={e=>setEditForm({...editForm,[k]:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
                  </div>
                ))}
                <div>
                  <label className="block text-xs text-gray-500 font-semibold mb-1">Unit</label>
                  <select value={editForm.unit||"per item"} onChange={e=>setEditForm({...editForm,unit:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none">
                    {["per kg","per item","per piece","per pair","per lb"].map(u=><option key={u}>{u}</option>)}
                  </select>
                </div>
                <div className="flex gap-3 mt-2">
                  <button onClick={saveEdit} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black hover:bg-indigo-700">Save ✅</button>
                  <button onClick={()=>setEditSvc(null)} className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Service Modal */}
        {previewSvc&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 text-white flex items-center justify-between">
                <div>
                  <h3 className="font-black text-lg">🌐 Website Preview</h3>
                  <p className="text-indigo-200 text-xs mt-0.5">How customers see this service</p>
                </div>
                <button onClick={()=>setPreviewSvc(null)} className="text-white/70 hover:text-white p-1 rounded-lg hover:bg-white/10"><X size={20}/></button>
              </div>
              <div className="p-5 max-h-[75vh] overflow-y-auto">
                <p className="text-xs text-gray-400 font-semibold mb-4 uppercase tracking-wide text-center">↓ Live replica of your website card ↓</p>
                <div className="border-2 border-indigo-200 rounded-2xl p-5 shadow-lg bg-gradient-to-br from-white to-indigo-50/30">
                  <div className="flex items-start justify-between mb-3">
                    <div className="text-5xl">{previewSvc.icon||"👔"}</div>
                    <span className={`text-[10px] font-bold px-2 py-1 rounded-full ${previewSvc.active?"bg-green-100 text-green-700":"bg-gray-100 text-gray-500"}`}>
                      {previewSvc.active?"● Live":"● Hidden"}
                    </span>
                  </div>
                  <h3 className="font-black text-xl text-gray-900 mb-1">{previewSvc.name}</h3>
                  {previewSvc.desc&&<p className="text-gray-500 text-sm mb-3 leading-relaxed">{previewSvc.desc}</p>}
                  <div className="flex items-end gap-1 mb-1">
                    <span className="font-black text-indigo-600 text-3xl">${Number(previewSvc.price||0).toLocaleString()}</span>
                    <span className="text-sm text-gray-400 font-normal mb-1">{previewSvc.unit}</span>
                  </div>
                  <p className="text-xs text-gray-400 mb-4">⏱ Ready in {previewSvc.duration}</p>
                  <div className="bg-indigo-50 border border-indigo-100 rounded-xl px-3 py-2 mb-4">
                    <p className="text-xs text-indigo-700 font-semibold">🛡️ 100% satisfaction guarantee</p>
                  </div>
                  <button className="w-full bg-indigo-600 text-white py-3 rounded-xl text-sm font-black shadow-md">
                    Book {previewSvc.name} →
                  </button>
                </div>
                <div className="flex gap-3 mt-4">
                  <button onClick={()=>{setPreviewSvc(null);openEdit(previewSvc);}} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl text-sm font-bold hover:bg-indigo-700 flex items-center justify-center gap-2">
                    <Edit size={14}/> Edit This
                  </button>
                  <button onClick={()=>setPreviewSvc(null)} className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl text-sm font-bold hover:bg-gray-50">Close</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // STAFF
  // ══════════════════════════════════════════════════════════
  function Staff() {
    const [selStaff, setSelStaff]   = useState(null);
    const [editStaff,setEditStaff]  = useState(null);
    const [editForm, setEditForm]   = useState({});

    function openEdit(s){setEditForm({...s});setEditStaff(s.id);}
    function saveEdit(){setStaff(prev=>prev.map(x=>x.id===editStaff?{...editForm}:x));setEditStaff(null);toast.success("Staff updated ✅");}

    const onDuty  = staff.filter(s=>s.status==="On Duty").length;
    const offDuty = staff.filter(s=>s.status==="Off Duty").length;
    const onLeave = staff.filter(s=>s.status==="On Leave").length;

    const shiftColors = { Morning:"bg-amber-100 text-amber-700", Afternoon:"bg-blue-100 text-blue-700", Evening:"bg-purple-100 text-purple-700", Night:"bg-gray-100 text-gray-700" };
    const statusConfig = {
      "On Duty": { cls:"bg-green-100 text-green-700",  dot:"bg-green-500"  },
      "Off Duty":{ cls:"bg-gray-100 text-gray-600",    dot:"bg-gray-400"   },
      "On Leave":{ cls:"bg-yellow-100 text-yellow-700",dot:"bg-yellow-500" },
    };
    const avatarColors = ["bg-indigo-500","bg-purple-500","bg-pink-500","bg-emerald-500","bg-orange-500","bg-sky-500"];

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="font-black text-base sm:text-lg text-gray-800">Staff Management</h3>
            <p className="text-xs text-gray-400 mt-0.5">Manage your team</p>
          </div>
          <button onClick={()=>setAddStaffOpen(true)} className="flex items-center gap-2 bg-indigo-600 text-white px-3 py-2 rounded-xl text-sm font-bold hover:bg-indigo-700 shadow-md"><Plus size={14}/>Add</button>
        </div>

        <div className="grid grid-cols-3 gap-3">
          {[
            {label:"On Duty",val:onDuty,bg:"bg-green-50",border:"border-green-100",dot:"bg-green-500",text:"text-green-700"},
            {label:"Off Duty",val:offDuty,bg:"bg-gray-50",border:"border-gray-100",dot:"bg-gray-400",text:"text-gray-600"},
            {label:"On Leave",val:onLeave,bg:"bg-yellow-50",border:"border-yellow-100",dot:"bg-yellow-500",text:"text-yellow-700"},
          ].map(s=>(
            <div key={s.label} className={`${s.bg} border ${s.border} rounded-2xl p-3 flex items-center gap-2`}>
              <div className={`w-2.5 h-2.5 rounded-full ${s.dot} shrink-0`}/>
              <div><p className={`text-xl sm:text-2xl font-black ${s.text}`}>{s.val}</p><p className="text-[10px] sm:text-xs text-gray-500">{s.label}</p></div>
            </div>
          ))}
        </div>

        {/* Staff cards — 1 col mobile, 2 sm, 3 lg */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {staff.map((s,i)=>{
            const sc=statusConfig[s.status]||statusConfig["Off Duty"];
            const av=avatarColors[i%avatarColors.length];
            return (
              <div key={s.id} onClick={()=>setSelStaff(s)}
                className="bg-white rounded-2xl border border-gray-100 hover:border-indigo-200 hover:shadow-lg transition-all cursor-pointer overflow-hidden">
                <div className={`h-1.5 w-full ${sc.dot==="bg-green-500"?"bg-gradient-to-r from-green-400 to-emerald-500":sc.dot==="bg-yellow-500"?"bg-gradient-to-r from-yellow-400 to-orange-400":"bg-gradient-to-r from-gray-300 to-gray-400"}`}/>
                <div className="p-4 sm:p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-12 h-12 rounded-2xl ${av} text-white flex items-center justify-center text-xl font-black shadow-md`}>{s.name[0]}</div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-black text-gray-900 truncate">{s.name}</h4>
                      <p className="text-gray-500 text-xs mt-0.5 truncate">{s.role}</p>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${sc.cls} shrink-0`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${sc.dot}`}/>{s.status}
                    </div>
                  </div>
                  <span className={`px-2.5 py-1 rounded-lg text-xs font-bold ${shiftColors[s.shift]||"bg-gray-100 text-gray-600"}`}>🕐 {s.shift}</span>
                  <div className="flex gap-2 mt-3" onClick={e=>e.stopPropagation()}>
                    <button onClick={()=>openEdit(s)} className="flex-1 flex items-center justify-center gap-1 text-xs border-2 border-indigo-200 text-indigo-600 py-2 rounded-xl hover:bg-indigo-50 font-bold"><Edit size={12}/>Edit</button>
                    <select value={s.status} onChange={e=>{setStaff(prev=>prev.map(x=>x.id===s.id?{...x,status:e.target.value}:x));toast.success(`${s.name} → ${e.target.value}`);}}
                      className="flex-1 text-xs border-2 border-gray-200 rounded-xl px-2 py-2 text-gray-600 focus:outline-none bg-white font-bold">
                      <option>On Duty</option><option>Off Duty</option><option>On Leave</option>
                    </select>
                    <button onClick={()=>{if(window.confirm(`Remove ${s.name}?`)){setStaff(prev=>prev.filter(x=>x.id!==s.id));toast.success("Removed");}}} className="p-2 rounded-xl bg-red-50 text-red-400 border-2 border-red-100"><Trash2 size={13}/></button>
                  </div>
                </div>
              </div>
            );
          })}
          <button onClick={()=>setAddStaffOpen(true)} className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-2xl p-5 hover:border-indigo-400 hover:bg-indigo-50 transition-all flex flex-col items-center justify-center gap-3 min-h-[160px] group">
            <div className="w-12 h-12 rounded-2xl bg-gray-200 group-hover:bg-indigo-100 flex items-center justify-center transition-colors"><Plus size={22} className="text-gray-400 group-hover:text-indigo-600"/></div>
            <p className="font-bold text-gray-500 group-hover:text-indigo-600 text-sm">Add Staff Member</p>
          </button>
        </div>

        {/* Staff profile modal */}
        {selStaff&&!editStaff&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl overflow-hidden">
              <div className={`px-6 py-5 text-white relative ${selStaff.status==="On Duty"?"bg-gradient-to-r from-green-500 to-emerald-600":selStaff.status==="On Leave"?"bg-gradient-to-r from-yellow-500 to-orange-500":"bg-gradient-to-r from-gray-500 to-gray-600"}`}>
                <button onClick={()=>setSelStaff(null)} className="absolute top-4 right-4 text-white/70 hover:text-white"><X size={20}/></button>
                <div className="flex items-center gap-4">
                  <div className={`w-16 h-16 rounded-2xl ${avatarColors[staff.indexOf(selStaff)%avatarColors.length]} flex items-center justify-center text-3xl font-black shadow-lg`}>{selStaff.name[0]}</div>
                  <div><h3 className="font-black text-xl">{selStaff.name}</h3><p className="text-white/80 text-sm">{selStaff.role}</p></div>
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-3">
                  {[["Status",selStaff.status,statusConfig[selStaff.status]?.cls||""],["Shift",selStaff.shift+" Shift",shiftColors[selStaff.shift]||""],["Role",selStaff.role,"bg-indigo-50 text-indigo-700"]].map(([k,v,cls])=>(
                    <div key={k} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                      <span className="text-gray-500 text-sm">{k}</span>
                      <span className={`text-xs font-bold px-3 py-1 rounded-full ${cls}`}>{v}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-500 uppercase mb-2">Update Status</p>
                  <div className="grid grid-cols-3 gap-2">
                    {["On Duty","Off Duty","On Leave"].map(st=>(
                      <button key={st} onClick={()=>{setStaff(prev=>prev.map(x=>x.id===selStaff.id?{...x,status:st}:x));setSelStaff(prev=>({...prev,status:st}));toast.success(`${selStaff.name} → ${st}`);}}
                        className={`py-2 rounded-xl text-xs font-bold transition-all ${selStaff.status===st?statusConfig[st]?.cls+" ring-2 ring-offset-1 ring-indigo-400":"bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>{st}</button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={()=>{setSelStaff(null);openEdit(selStaff);}} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black text-sm hover:bg-indigo-700 flex items-center justify-center gap-2"><Edit size={14}/>Edit</button>
                  <button onClick={()=>{if(window.confirm(`Remove ${selStaff.name}?`)){setStaff(prev=>prev.filter(x=>x.id!==selStaff.id));setSelStaff(null);toast.success("Removed");}}} className="px-4 py-3 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 border-2 border-red-100"><Trash2 size={16}/></button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit staff modal */}
        {editStaff&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
              <div className="bg-indigo-600 rounded-t-3xl sm:rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
                <h3 className="font-black text-lg">Edit Staff</h3>
                <button onClick={()=>setEditStaff(null)} className="text-white/70 hover:text-white"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {[["Full Name","name","text"],["Role","role","text"]].map(([ph,k,t])=>(
                  <div key={k}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{ph}</label>
                    <input type={t} value={editForm[k]||""} onChange={e=>setEditForm({...editForm,[k]:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
                  </div>
                ))}
                {[["Shift",["Morning","Afternoon","Evening","Night"]],["Status",["On Duty","Off Duty","On Leave"]]].map(([label,opts])=>(
                  <div key={label}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{label}</label>
                    <select value={editForm[label.toLowerCase()]||opts[0]} onChange={e=>setEditForm({...editForm,[label.toLowerCase()]:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none">
                      {opts.map(o=><option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <button onClick={saveEdit} className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black">Save ✅</button>
                  <button onClick={()=>setEditStaff(null)} className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold">Cancel</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add staff modal */}
        {addStaffOpen&&(
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
              <div className="bg-indigo-600 rounded-t-3xl sm:rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
                <h3 className="font-black text-lg">Add Staff Member</h3>
                <button onClick={()=>setAddStaffOpen(false)} className="text-white/70 hover:text-white"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
                {[["Full Name *","name","text"],["Role *","role","text"]].map(([ph,k,t])=>(
                  <div key={k}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{ph}</label>
                    <input type={t} placeholder={ph} value={newStaff[k]} onChange={e=>setNewStaff({...newStaff,[k]:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
                  </div>
                ))}
                {[["Shift",["Morning","Afternoon","Evening","Night"]],["Status",["On Duty","Off Duty","On Leave"]]].map(([label,opts])=>(
                  <div key={label}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{label}</label>
                    <select value={newStaff[label.toLowerCase()]} onChange={e=>setNewStaff({...newStaff,[label.toLowerCase()]:e.target.value})} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none">
                      {opts.map(o=><option key={o}>{o}</option>)}
                    </select>
                  </div>
                ))}
                <button onClick={handleAddStaff} className="w-full bg-indigo-600 text-white py-3.5 rounded-xl font-black hover:bg-indigo-700 mt-2">Add Staff Member →</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // SETTINGS
  // ══════════════════════════════════════════════════════════
  function SettingsPanel() {
    const [biz,setBiz]=useState({
      name:businessConfig.name, phone:businessConfig.phone, email:businessConfig.email, address:businessConfig.address,
      hours:Array.isArray(businessConfig.hours)?businessConfig.hours.map(h=>`${h.day}: ${h.time}`).join(", "):businessConfig.hours,
    });
    const [saved,setSaved]=useState(false);
    const [pw,setPw]=useState({newPw:"",confirmPw:""});

    return (
      <div className="space-y-4 max-w-2xl">
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="font-bold text-sm mb-4">🏢 Business Information</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[["Business Name","name","text"],["Phone","phone","tel"],["Email","email","email"],["Address","address","text"],["Hours","hours","text"]].map(([label,key,type])=>(
              <div key={key} className={key==="address"||key==="hours"?"sm:col-span-2":""}>
                <label className="block text-xs text-gray-500 font-medium mb-1">{label}</label>
                <input type={type} value={biz[key]} onChange={e=>setBiz({...biz,[key]:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
              </div>
            ))}
          </div>
          <button onClick={()=>{setSaved(true);toast.success("Saved ✅");setTimeout(()=>setSaved(false),3000);}}
            className={`mt-4 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 ${saved?"bg-green-600 text-white":"bg-indigo-600 text-white hover:bg-indigo-700"}`}>
            {saved?<><Check size={14}/>Saved!</>:"Save Changes"}
          </button>
        </div>
        <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
          <h3 className="font-bold text-sm mb-4">🔐 Change Admin Password</h3>
          <div className="space-y-3">
            {[["New Password","newPw"],["Confirm Password","confirmPw"]].map(([ph,k])=>(
              <div key={k}>
                <label className="block text-xs text-gray-500 font-medium mb-1">{ph}</label>
                <input type="password" placeholder={ph} value={pw[k]} onChange={e=>setPw({...pw,[k]:e.target.value})} className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
              </div>
            ))}
          </div>
          <button onClick={()=>{
            if(!pw.newPw){toast.error("Enter a password");return;}
            if(pw.newPw!==pw.confirmPw){toast.error("Passwords do not match");return;}
            if(pw.newPw.length<8){toast.error("Min 8 characters");return;}
            toast.success("Password updated ✅");setPw({newPw:"",confirmPw:""});
          }} className="mt-4 bg-indigo-600 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-indigo-700 flex items-center gap-2">
            <Lock size={14}/>Update Password
          </button>
        </div>
        <div className="bg-white p-5 rounded-2xl border-2 border-red-100 shadow-sm">
          <h3 className="font-bold text-sm mb-1 text-red-600">⚠️ Danger Zone</h3>
          <p className="text-xs text-gray-400 mb-4">These actions cannot be undone.</p>
          <div className="flex flex-wrap gap-3">
            <button onClick={()=>toast.error("Contact your developer")} className="border border-red-400 text-red-500 px-4 py-2 rounded-xl text-sm hover:bg-red-50">Clear All Orders</button>
            <button onClick={()=>toast.error("Contact your developer")} className="border border-red-400 text-red-500 px-4 py-2 rounded-xl text-sm hover:bg-red-50">Reset All Data</button>
          </div>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // MESSAGES — Full real-time chat between admin and customers
  // Firestore structure:
  //   conversations/{id} — { customerName, customerPhone,
  //     customerEmail, lastMessage, lastAt, adminUnread }
  //   conversations/{id}/messages/{msgId} — { text, from,
  //     createdAt, read }
  // The customer frontend writes to the same collection.
  // ══════════════════════════════════════════════════════════
  function Messages() {
    const [newConvOpen,  setNewConvOpen]  = useState(false);
    const [newName,      setNewName]      = useState("");
    const [newPhone,     setNewPhone]     = useState("");
    const [newEmail,     setNewEmail]     = useState("");
    const [convSearch,   setConvSearch]   = useState("");

    const activeConv = conversations.find(c => c.id === activeConvId);

    const filteredConvs = conversations.filter(c =>
      !convSearch ||
      c.customerName?.toLowerCase().includes(convSearch.toLowerCase()) ||
      c.customerPhone?.includes(convSearch)
    );

    async function handleStartConversation() {
      const id = await startNewConversation(newName, newPhone, newEmail);
      if (id) {
        setActiveConvId(id);
        setMobileShowThread(true);
        setNewConvOpen(false);
        setNewName(""); setNewPhone(""); setNewEmail("");
        toast.success("Conversation started!");
      }
    }

    function handleKeyDown(e) {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        sendAdminMessage();
      }
    }

    // Format timestamp for chat bubbles
    function msgTime(ts) {
      if (!ts) return "";
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }

    // Group messages by date
    function msgDate(ts) {
      if (!ts) return "";
      const d = ts?.toDate ? ts.toDate() : new Date(ts);
      const today = new Date();
      const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
      if (d.toDateString() === today.toDateString()) return "Today";
      if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
      return d.toLocaleDateString("en-US", { weekday:"short", month:"short", day:"numeric" });
    }

    return (
      <div className="flex h-[calc(100vh-3.5rem-5rem)] md:h-[calc(100vh-3.5rem)] bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">

        {/* ── LEFT: Conversation list ── */}
        <div className={`${mobileShowThread ? "hidden" : "flex"} md:flex flex-col w-full md:w-80 border-r border-gray-100 shrink-0`}>

          {/* Header */}
          <div className="p-4 border-b bg-gradient-to-r from-indigo-600 to-indigo-700 text-white flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare size={18}/>
              <span className="font-bold text-sm">Messages</span>
              {totalUnreadMsgs > 0 && (
                <span className="bg-green-400 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{totalUnreadMsgs} unread</span>
              )}
            </div>
            <button
              onClick={() => setNewConvOpen(true)}
              className="w-8 h-8 rounded-xl bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              title="New conversation">
              <Plus size={16}/>
            </button>
          </div>

          {/* Search */}
          <div className="p-3 border-b">
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2">
              <Search size={13} className="text-gray-400 shrink-0"/>
              <input value={convSearch} onChange={e=>setConvSearch(e.target.value)}
                placeholder="Search conversations…" className="text-sm outline-none flex-1 bg-transparent text-gray-700 placeholder-gray-400"/>
              {convSearch && <button onClick={()=>setConvSearch("")} className="text-gray-400"><X size={12}/></button>}
            </div>
          </div>

          {/* Conversation list */}
          <div className="flex-1 overflow-y-auto">
            {convLoading ? (
              <div className="p-8 text-center">
                <RefreshCw size={20} className="animate-spin text-indigo-400 mx-auto mb-2"/>
                <p className="text-gray-400 text-xs">Loading conversations…</p>
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare size={36} className="text-gray-200 mx-auto mb-3"/>
                <p className="text-gray-500 font-bold text-sm">No conversations yet</p>
                <p className="text-gray-400 text-xs mt-1">Start a new one or wait for a customer to reach out</p>
                <button onClick={()=>setNewConvOpen(true)}
                  className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold hover:bg-indigo-700 flex items-center gap-2 mx-auto">
                  <Plus size={13}/> New Conversation
                </button>
              </div>
            ) : (
              filteredConvs.map(c => (
                <button key={c.id}
                  onClick={() => { setActiveConvId(c.id); setMobileShowThread(true); }}
                  className={`w-full flex items-start gap-3 px-4 py-3.5 border-b border-gray-50 hover:bg-indigo-50/40 transition-colors text-left ${activeConvId===c.id?"bg-indigo-50 border-l-4 border-l-indigo-500":""}`}>
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    <div className="w-11 h-11 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white flex items-center justify-center text-base font-black shadow-sm">
                      {c.customerName?.[0]?.toUpperCase() || "?"}
                    </div>
                    {/* Online/unread dot */}
                    {(c.adminUnread || 0) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white text-white text-[8px] flex items-center justify-center font-black">
                        {c.adminUnread > 9 ? "9+" : c.adminUnread}
                      </span>
                    )}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <p className={`text-sm truncate ${(c.adminUnread||0)>0?"font-black text-gray-900":"font-semibold text-gray-800"}`}>
                        {c.customerName || "Unknown"}
                      </p>
                      <p className="text-[10px] text-gray-400 shrink-0 ml-2">{timeAgo(c.lastAt)}</p>
                    </div>
                    <p className="text-xs text-gray-400 truncate">{c.customerPhone}</p>
                    <p className={`text-xs truncate mt-0.5 ${(c.adminUnread||0)>0?"text-indigo-600 font-semibold":"text-gray-400"}`}>
                      {c.lastMessage || "No messages yet"}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── RIGHT: Chat thread ── */}
        <div className={`${!mobileShowThread ? "hidden" : "flex"} md:flex flex-col flex-1 min-w-0`}>

          {activeConvId && activeConv ? (
            <>
              {/* Thread header */}
              <div className="px-4 py-3 border-b bg-white flex items-center gap-3 shrink-0 shadow-sm">
                {/* Back button — mobile only */}
                <button onClick={()=>setMobileShowThread(false)} className="md:hidden p-1.5 rounded-xl hover:bg-gray-100 text-gray-500">
                  <ChevronLeft size={20}/>
                </button>
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white flex items-center justify-center text-sm font-black shrink-0">
                  {activeConv.customerName?.[0]?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-gray-900 text-sm">{activeConv.customerName}</p>
                  <p className="text-xs text-gray-400">{activeConv.customerPhone}</p>
                </div>
                {/* Quick contact actions */}
                <div className="flex items-center gap-1">
                  <a href={`tel:${activeConv.customerPhone}`}
                    className="p-2 rounded-xl bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors"
                    title="Call customer">
                    <PhoneCall size={15}/>
                  </a>
                  {activeConv.customerPhone && (
                    <a href={`https://wa.me/${(activeConv.customerPhone||"").replace(/\D/g,"")}?text=Hi ${activeConv.customerName}, this is 1464 Country Cleaners!`}
                      target="_blank" rel="noopener noreferrer"
                      className="p-2 rounded-xl bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                      title="WhatsApp">
                      <span className="text-sm">💬</span>
                    </a>
                  )}
                  {/* Link to customer orders */}
                  <button
                    onClick={() => navigateTo("customers")}
                    className="p-2 rounded-xl bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors text-xs font-bold"
                    title="View customer profile">
                    <Eye size={15}/>
                  </button>
                </div>
              </div>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2" style={{background:"#f8fafc"}}>
                {msgLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <RefreshCw size={20} className="animate-spin text-indigo-400"/>
                  </div>
                ) : messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <div className="text-5xl mb-3">👋</div>
                    <p className="text-gray-500 font-bold text-sm">No messages yet</p>
                    <p className="text-gray-400 text-xs mt-1">Send a message to start the conversation</p>
                  </div>
                ) : (
                  (() => {
                    let lastDate = "";
                    return messages.map((m, i) => {
                      const isAdmin   = m.from === "admin";
                      const dateLabel = msgDate(m.createdAt);
                      const showDate  = dateLabel !== lastDate;
                      if (showDate) lastDate = dateLabel;
                      return (
                        <div key={m.id}>
                          {/* Date divider */}
                          {showDate && (
                            <div className="flex items-center gap-3 my-3">
                              <div className="flex-1 h-px bg-gray-200"/>
                              <span className="text-[10px] text-gray-400 font-semibold bg-white px-2 py-0.5 rounded-full border border-gray-200 shrink-0">
                                {dateLabel}
                              </span>
                              <div className="flex-1 h-px bg-gray-200"/>
                            </div>
                          )}
                          {/* Bubble */}
                          <div className={`flex ${isAdmin ? "justify-end" : "justify-start"} mb-1`}>
                            {/* Customer avatar — left side */}
                            {!isAdmin && (
                              <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 text-white flex items-center justify-center text-xs font-black shrink-0 mr-2 self-end mb-1">
                                {activeConv.customerName?.[0]?.toUpperCase() || "?"}
                              </div>
                            )}
                            <div className={`max-w-[75%] sm:max-w-[65%]`}>
                              <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                                isAdmin
                                  ? "bg-indigo-600 text-white rounded-br-sm"
                                  : "bg-white text-gray-800 border border-gray-100 rounded-bl-sm"
                              }`}>
                                {m.text}
                              </div>
                              <p className={`text-[10px] mt-1 ${isAdmin ? "text-right text-gray-400" : "text-left text-gray-400"}`}>
                                {msgTime(m.createdAt)}
                                {isAdmin && (
                                  <span className="ml-1">
                                    {m.read ? "✓✓" : "✓"}
                                  </span>
                                )}
                              </p>
                            </div>
                            {/* Admin avatar — right side */}
                            {isAdmin && (
                              <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs font-black shrink-0 ml-2 self-end mb-1">
                                A
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()
                )}
                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef}/>
              </div>

              {/* Message input */}
              <div className="p-3 border-t bg-white shrink-0">
                <div className="flex items-end gap-2">
                  <div className="flex-1 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-2.5 focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-100 transition-all">
                    <textarea
                      value={msgInput}
                      onChange={e => setMsgInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="Type a message… (Enter to send)"
                      rows={1}
                      className="w-full text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400 resize-none max-h-28 overflow-y-auto"
                      style={{lineHeight:"1.5"}}
                    />
                  </div>
                  <button
                    onClick={sendAdminMessage}
                    disabled={!msgInput.trim()}
                    className="w-10 h-10 rounded-2xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed shadow-md shrink-0">
                    <Send size={16}/>
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5 text-center">Enter to send · Shift+Enter for new line</p>
              </div>
            </>
          ) : (
            /* No conversation selected */
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-gray-50">
              <div className="w-20 h-20 rounded-3xl bg-indigo-100 flex items-center justify-center mb-4 shadow-inner">
                <MessageSquare size={36} className="text-indigo-400"/>
              </div>
              <h3 className="font-black text-gray-800 text-lg mb-2">Select a conversation</h3>
              <p className="text-gray-500 text-sm max-w-xs mb-6">
                Choose a conversation from the left to view messages, or start a new one with a customer.
              </p>
              <button onClick={()=>setNewConvOpen(true)}
                className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-bold text-sm hover:bg-indigo-700 flex items-center gap-2 shadow-md">
                <Plus size={15}/> Start New Conversation
              </button>
              <div className="mt-8 p-4 bg-amber-50 border border-amber-200 rounded-2xl max-w-xs">
                <p className="text-xs text-amber-700 font-semibold mb-1">📱 For customers to message you:</p>
                <p className="text-xs text-amber-600">Add a chat widget to your website that writes to Firestore <code className="bg-amber-100 px-1 rounded">conversations</code> collection. Customer messages appear here in real-time.</p>
              </div>
            </div>
          )}
        </div>

        {/* ── New Conversation Modal ── */}
        {newConvOpen && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
            <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full sm:max-w-sm shadow-2xl">
              <div className="bg-indigo-600 rounded-t-3xl sm:rounded-t-2xl px-6 py-4 text-white flex items-center justify-between">
                <div>
                  <h3 className="font-black text-lg">💬 New Conversation</h3>
                  <p className="text-indigo-200 text-xs mt-0.5">Start chatting with a customer</p>
                </div>
                <button onClick={()=>setNewConvOpen(false)} className="text-white/70 hover:text-white p-1"><X size={20}/></button>
              </div>
              <div className="p-5 space-y-3">
                {/* Auto-fill from existing customer */}
                {customers.length > 0 && (
                  <div>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">Quick select from customers</label>
                    <select
                      onChange={e => {
                        const c = customers.find(x => x.id === e.target.value);
                        if (c) { setNewName(c.name||""); setNewPhone(c.phone||""); setNewEmail(c.email||""); }
                      }}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400 bg-white">
                      <option value="">— Select a customer —</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name} · {c.phone}</option>)}
                    </select>
                  </div>
                )}
                <div className="border-t pt-3">
                  <label className="block text-xs text-gray-500 font-semibold mb-1">Or enter manually</label>
                </div>
                {[["Customer Name *","name","text",newName,setNewName],
                  ["Phone Number *","phone","tel",newPhone,setNewPhone],
                  ["Email (optional)","email","email",newEmail,setNewEmail]
                ].map(([ph,k,t,val,setter]) => (
                  <div key={k}>
                    <label className="block text-xs text-gray-500 font-semibold mb-1">{ph}</label>
                    <input type={t} placeholder={ph} value={val} onChange={e=>setter(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-indigo-400"/>
                  </div>
                ))}
                <div className="flex gap-3 pt-1">
                  <button onClick={handleStartConversation}
                    className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-black hover:bg-indigo-700 flex items-center justify-center gap-2">
                    <MessageSquare size={15}/> Start Chat
                  </button>
                  <button onClick={()=>setNewConvOpen(false)}
                    className="flex-1 border-2 border-gray-200 text-gray-600 py-3 rounded-xl font-bold hover:bg-gray-50">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }



  const sections = {
    overview:<Overview/>, orders:<Orders/>, customers:<Customers/>,
    messages:<Messages/>, services:<Services/>, staff:<Staff/>, settings:<SettingsPanel/>,
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <style>{`
        @keyframes fadeIn  { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeOut { from { opacity:1; transform:translateY(0); } to { opacity:0; transform:translateY(-6px); } }
        .page-fade-in  { animation: fadeIn  0.25s ease-out forwards; }
        .page-fade-out { animation: fadeOut 0.25s ease-in  forwards; }
      `}</style>
      <Sidebar/>
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar/>
        <main className={`flex-1 overflow-hidden ${section==="messages"?"":"overflow-y-auto p-3 sm:p-4 md:p-6 pb-20 md:pb-6"}`}>
          <div key={section} className={`h-full ${isTransitioning?"page-fade-out":"page-fade-in"}`}>
            {sections[section]}
          </div>
        </main>
      </div>
      <BottomNav/>
    </div>
  );
}